/**
 * Team members table component for project billing rate overview.
 *
 * This component displays project team members in a tabular format with their
 * associated billing rates and rate sources. It provides functionality for
 * adding new team members through a searchable dropdown interface.
 *
 * Key Features:
 * - Displays team members with rate information in organized columns
 * - Supports adding new team members via search dropdown with cursor-based pagination
 * - Shows appropriate empty states when no team members are present
 * - Adapts behavior based on read-only mode and existing team member count
 * - Integrates with React Table for consistent table rendering
 * - Provides user-friendly interface for team member management
 *
 * State Management:
 * - Empty state: Shows message and add member dropdown when no members exist
 * - Populated state: Shows table with data and add member row at bottom
 * - Read-only state: Hides all add member functionality
 *
 * The component uses a "last row children" pattern to render additional UI
 * elements (empty state, add member controls) as table rows for consistent styling.
 */
import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import {
  DropdownSearchItem,
  DropdownWithSearchAndPagination,
} from '@/modules/common/components/molecules/selectors/dropdown';
import { ProjectTeamMember } from '@/modules/projects/types/project-team-member';

/**
 * Props interface for the TeamMembersTable component.
 * Includes table configuration, interaction handlers, and state flags.
 */
interface ProjectRatesOverviewTableProps {
  /** Configured React Table instance with team member data and columns */
  table: Table<ProjectTeamMember>;
  /** Whether the table should be in read-only mode (no add/edit functionality) */
  isReadMode: boolean;
  /** Function to load/search team members with cursor-based pagination */
  loadMembers: (searchString?: string, cursor?: number) => Promise<any>;
  /** Callback when a team member is selected from the dropdown */
  onAddSelectedTeamMember: (item: DropdownSearchItem) => void;
  /** Whether the table currently has any team members */
  hasTeamMembers: boolean;
  /** Number of team members currently selected (used to invalidate dropdown cache) */
  teamMembersCount: number;
}

/**
 * Team members table component with integrated member management.
 *
 * This component orchestrates the display of team members in a table format while
 * providing contextual UI for adding new members. It adapts its presentation based
 * on current state (empty vs populated) and user permissions (read vs edit mode).
 *
 * Rendering Logic:
 * - Empty state: Full-height message with prominent add member button
 * - Populated state: Data table with add member row at bottom
 * - Read-only state: Table display only, no add member functionality
 *
 * The component uses the DataTable's "lastRowChildren" pattern to seamlessly
 * integrate additional UI elements as table rows, maintaining consistent styling
 * and layout with the rest of the table.
 */
export const ProjectRatesOverviewTable: React.FC<ProjectRatesOverviewTableProps> = ({
  table,
  isReadMode,
  loadMembers,
  onAddSelectedTeamMember,
  hasTeamMembers,
  teamMembersCount,
}) => {
  const { t } = useTranslation(['authenticated.projects', 'common']);

  /**
   * Renders the empty state when no team members are present.
   * Shows a centered message with prominent add member button.
   * Only displayed when hasTeamMembers is false.
   */
  const renderEmptyState = (): React.ReactNode => {
    if (!hasTeamMembers) {
      return (
        <tr>
          <td colSpan={table.getAllColumns().length} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start gap-3 px-8 text-left">
              <div className="flex flex-col gap-0.5">
                <div className="text-sm font-semibold text-foreground">
                  {t('details.tabs.billing.noTeamMembersMessage')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('details.tabs.billing.noTeamMembersSubMessage')}
                </div>
              </div>
              <DropdownWithSearchAndPagination
                onSelectItem={onAddSelectedTeamMember}
                loadItems={loadMembers}
                searchPlaceholder={t('details.tabs.billing.searchUsers')}
                emptyMessage={t('details.tabs.billing.noUsersFound')}
                loadingMessage={t('details.tabs.billing.loadingUsers')}
                disabled={isReadMode}
                doShowAvatar={true}
                doShowSecondaryText={true}
                excludedItemsCount={teamMembersCount}
                buttonVariant="default"
              >
                <CirclePlus className="h-4 w-4" />
                {t('details.tabs.billing.addMember')}
              </DropdownWithSearchAndPagination>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  /**
   * Renders the add member row when team members exist.
   * Provides a compact add member interface at the bottom of the table.
   * Only shown in edit mode when team members are present.
   */
  const renderAddMemberRow = (): React.ReactNode => {
    if (!isReadMode && hasTeamMembers) {
      return (
        <tr className="hover:bg-transparent">
          <td colSpan={table.getAllColumns().length} className="h-16 px-4">
            <div className="flex justify-start">
              <DropdownWithSearchAndPagination
                onSelectItem={onAddSelectedTeamMember}
                loadItems={loadMembers}
                searchPlaceholder={t('details.tabs.billing.searchUsers')}
                emptyMessage={t('details.tabs.billing.noUsersFound')}
                loadingMessage={t('details.tabs.billing.loadingUsers')}
                disabled={isReadMode}
                doShowAvatar={true}
                doShowSecondaryText={true}
                excludedItemsCount={teamMembersCount}
              >
                <CirclePlus className="h-4 w-4" />
                {t('details.tabs.billing.addMember')}
              </DropdownWithSearchAndPagination>
            </div>
          </td>
        </tr>
      );
    }

    return null;
  };

  // Combine both conditional row elements for DataTable integration
  const lastRowChildren = (
    <>
      {renderEmptyState()}
      {renderAddMemberRow()}
    </>
  );

  return <DataTable table={table} lastRowChildren={lastRowChildren} />;
};
